import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { verifyEmail, resendVerificationEmail } from '../../services/authService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import toast, { Toaster } from 'react-hot-toast';

const VerifyEmailRequired = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile, userRole, loading, isEmailVerified, refreshProfile, signOut } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('pending'); // pending, success, error
  const token = searchParams?.get('token');

  useEffect(() => {
    if (token) {
      handleVerifyToken(token);
    }
  }, [token]);

  // When Supabase processes the #access_token hash from the email link,
  // onAuthStateChange fires SIGNED_IN and sets email_confirmed_at.
  // Detect that and redirect to the appropriate dashboard automatically.
  useEffect(() => {
    if (loading) return;
    if (isEmailVerified && verificationStatus === 'pending') {
      setVerificationStatus('success');
      setTimeout(() => {
        if (userRole === 'admin') {
          navigate('/admin-dashboard');
        } else if (userRole === 'expert') {
          navigate('/expert-dashboard');
        } else {
          navigate('/client-dashboard');
        }
      }, 1500);
    }
  }, [isEmailVerified, loading, verificationStatus, userRole, navigate]);

  const handleVerifyToken = async (verificationToken) => {
    setIsVerifying(true);
    
    try {
      const result = await verifyEmail(verificationToken);

      if (result?.success) {
        setVerificationStatus('success');
        toast?.success('Email verified successfully!');
        await refreshProfile();
        
        setTimeout(() => {
          // Redirect based on role
          if (userRole === 'admin') {
            navigate('/admin-dashboard');
          } else if (userRole === 'expert') {
            navigate('/expert-dashboard');
          } else {
            navigate('/client-dashboard');
          }
        }, 2000);
      } else {
        setVerificationStatus('error');
        toast?.error(result?.error || 'Verification failed');
      }
    } catch (error) {
      setVerificationStatus('error');
      toast?.error('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendEmail = async () => {
    if (!user) {
      toast?.error('Please log in to resend verification email');
      return;
    }

    setIsResending(true);

    try {
      const result = await resendVerificationEmail(
        user?.id,
        user?.email,
        userProfile?.full_name
      );

      if (result?.success) {
        toast?.success('Verification email sent! Please check your inbox.');
      } else {
        toast?.error(result?.error || 'Failed to send email');
      }
    } catch (error) {
      toast?.error('Failed to send email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground text-lg">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-success/10 via-background to-success/5 px-4">
        <Toaster position="top-center" />
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success/10 mb-6">
            <Icon name="CheckCircle" className="w-10 h-10 text-success" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-4">Email Verified!</h1>
          <p className="text-foreground-secondary mb-6">
            Your email has been successfully verified. Redirecting to dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-warning/10 via-background to-warning/5 px-4">
      <Toaster position="top-center" />
      
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-warning/10 mb-6">
            <Icon name="Mail" className="w-10 h-10 text-warning" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-4">Verify Your Email</h1>
          <p className="text-foreground-secondary">
            We've sent a verification link to{' '}
            <span className="font-medium text-foreground">{user?.email}</span>
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border space-y-6">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start">
              <Icon name="Info" className="w-5 h-5 text-primary mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-foreground-secondary">
                <p className="font-medium text-foreground mb-1">Check your inbox</p>
                <p>Click the verification link in the email to activate your account. The link expires in 24 hours.</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleResendEmail}
              disabled={isResending}
              variant="outline"
              className="w-full"
            >
              {isResending ? (
                <span className="flex items-center justify-center">
                  <Icon name="Loader2" className="w-5 h-5 animate-spin mr-2" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Icon name="RefreshCw" className="w-5 h-5 mr-2" />
                  Resend Verification Email
                </span>
              )}
            </Button>

            <Button
              onClick={handleSignOut}
              variant="ghost"
              className="w-full"
            >
              Sign Out
            </Button>
          </div>

          <div className="pt-4 border-t border-border">
            <p className="text-xs text-foreground-secondary text-center">
              Didn't receive the email? Check your spam folder or try resending.
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-foreground-secondary hover:text-foreground flex items-center justify-center">
            <Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailRequired;